import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Radio, Search, RefreshCw, Download, XCircle, Trash2, CalendarIcon, Filter } from "lucide-react";

export const M3uHistory = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);
  const { token } = useAuth();

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/admin/m3u-history?limit=500`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch {
      toast.error("Erreur chargement historique M3U");
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const clearHistory = async () => {
    try {
      setClearing(true);
      await axios.delete(`${API}/admin/m3u-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Historique M3U efface");
      setShowClearDialog(false);
      fetchHistory();
    } catch {
      toast.error("Erreur suppression historique");
    } finally {
      setClearing(false);
    }
  };

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setStatusFilter("all");
    setSearch("");
  };

  const filtered = logs.filter((l) => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      String(l.telegram_id || "").includes(s) ||
      (l.telegram_username || "").toLowerCase().includes(s) ||
      (l.m3u_link || "").toLowerCase().includes(s) ||
      (l.server || "").toLowerCase().includes(s) ||
      (l.status || "").toLowerCase().includes(s)
    );
  });

  const formatDate = (d) => {
    if (!d) return "-";
    try {
      const dt = new Date(d);
      return dt.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return d;
    }
  };

  const hasFilters = dateFrom || dateTo || statusFilter !== "all" || search;

  return (
    <div className="space-y-4" data-testid="m3u-history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Historique Bot M3U</h2>
          <p className="text-sm text-muted-foreground">{total} requete(s) au total</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowClearDialog(true)}
            disabled={total === 0}
            data-testid="clear-m3u-history"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Effacer
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchHistory}
            disabled={loading}
            data-testid="refresh-m3u-history"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Rafraichir
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-3 px-4">
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground mb-1 block">Recherche</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="ID Telegram, username, lien..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-9"
                  data-testid="m3u-history-search"
                />
              </div>
            </div>
            <div className="w-[160px]">
              <label className="text-xs text-muted-foreground mb-1 block">Date debut</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9"
                data-testid="filter-date-from"
              />
            </div>
            <div className="w-[160px]">
              <label className="text-xs text-muted-foreground mb-1 block">Date fin</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9"
                data-testid="filter-date-to"
              />
            </div>
            <div className="w-[130px]">
              <label className="text-xs text-muted-foreground mb-1 block">Statut</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="OK">OK</SelectItem>
                  <SelectItem value="FAIL">FAIL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9" data-testid="reset-filters">
                <XCircle className="h-4 w-4 mr-1" />
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Radio className="h-4 w-4" />
            Requetes ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-340px)]">
            <div className="overflow-x-auto">
              <div className="min-w-[700px]">
              <table className="w-full text-sm" data-testid="m3u-history-table">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Date</th>
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Telegram ID</th>
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">User</th>
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Lien M3U</th>
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Statut</th>
                    <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-muted-foreground">
                        {loading ? "Chargement..." : "Aucun historique"}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((log, i) => (
                      <tr
                        key={i}
                        className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                        data-testid={`m3u-history-row-${i}`}
                      >
                        <td className="py-2 px-4 whitespace-nowrap text-xs">
                          {log.date_str || formatDate(log.date)}
                        </td>
                        <td className="py-2 px-4 font-mono text-xs">{log.telegram_id}</td>
                        <td className="py-2 px-4">
                          <span className="text-xs">@{log.telegram_username || "-"}</span>
                        </td>
                        <td className="py-2 px-4 font-mono text-xs max-w-[400px]">
                          <span className="break-all select-all" title={log.m3u_link || "-"}>
                            {log.m3u_link || "-"}
                          </span>
                        </td>
                        <td className="py-2 px-4">
                          {log.status === "OK" ? (
                            <Badge
                              variant="outline"
                              className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                            >
                              <Download className="h-3 w-3 mr-1" />
                              OK
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-red-500/10 text-red-400 border-red-500/30"
                            >
                              <XCircle className="h-3 w-3 mr-1" />
                              FAIL
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 px-4 text-xs text-muted-foreground max-w-[250px] truncate">
                          {log.details || "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Confirm clear dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Effacer l'historique M3U</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Cette action va supprimer <strong>{total}</strong> entree(s) de l'historique du bot M3U.
            Cette action est irreversible.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearDialog(false)} data-testid="cancel-clear">
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={clearHistory}
              disabled={clearing}
              data-testid="confirm-clear"
            >
              {clearing ? "Suppression..." : "Confirmer la suppression"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
